/**
 * Frontend bandwidth display normalization.
 *
 * Mirrors the backend helper (services/influxdb/bandwidth-unit.js) so the chart
 * renders readable values even when the API returns raw, sub-Mbps numbers in
 * Mbps (older/undeployed backend). It is idempotent: values are first converted
 * back to a Mbps base using the unit declared by the API, so re-normalizing an
 * already-normalized payload (e.g. one the backend already scaled to Kbps)
 * produces the same result instead of double-scaling.
 *
 * Decision table (base values are in Mbps):
 *   max >= 1000 -> Gbps (x0.001)
 *   max >= 1    -> Mbps (x1)
 *   max  > 0    -> Kbps (x1000)
 *   max == 0    -> Mbps (default)
 */

export interface BandwidthPoint {
  time: string;
  value: number | null;
}

export type BandwidthUnit = "Kbps" | "Mbps" | "Gbps";

const ROUND_DECIMALS = 2;

const UNIT_TO_MBPS: Record<string, number> = {
  Kbps: 1 / 1000,
  Mbps: 1,
  Gbps: 1000,
};

function toMbpsBase(value: number | null | undefined, unit: string): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const factor = UNIT_TO_MBPS[unit] ?? 1;
  return value * factor;
}

function maxAbsAcrossSeries(
  seriesList: (BandwidthPoint[] | undefined)[],
  unit: string
): number {
  let max = 0;
  for (const series of seriesList) {
    if (!Array.isArray(series)) continue;
    for (const point of series) {
      const v = toMbpsBase(point?.value, unit);
      if (v === null) continue;
      const abs = Math.abs(v);
      if (abs > max) max = abs;
    }
  }
  return max;
}

function chooseUnit(maxMbps: number): { unit: BandwidthUnit; scale: number } {
  if (!Number.isFinite(maxMbps) || maxMbps <= 0) return { unit: "Mbps", scale: 1 };
  if (maxMbps >= 1000) return { unit: "Gbps", scale: 0.001 };
  if (maxMbps >= 1) return { unit: "Mbps", scale: 1 };
  return { unit: "Kbps", scale: 1000 };
}

function scaleSeries(
  series: BandwidthPoint[] | undefined,
  fromUnit: string,
  scale: number
): BandwidthPoint[] {
  if (!Array.isArray(series)) return [];
  return series.map((point) => {
    if (!point) return point;
    const base = toMbpsBase(point.value, fromUnit);
    if (base === null) return { ...point, value: null };
    return {
      ...point,
      value: parseFloat((base * scale).toFixed(ROUND_DECIMALS)),
    };
  });
}

/**
 * Normalize a single port/cloud/internet bandwidth payload for display.
 * Picks the best unit across inbound/outbound and rescales + rounds both.
 */
export function normalizePortBandwidthForDisplay(payload: {
  inbound?: BandwidthPoint[];
  outbound?: BandwidthPoint[];
  unit?: string;
} = {}): { unit: BandwidthUnit; inbound: BandwidthPoint[]; outbound: BandwidthPoint[] } {
  const fromUnit = payload.unit ?? "Mbps";
  const inbound = Array.isArray(payload.inbound) ? payload.inbound : [];
  const outbound = Array.isArray(payload.outbound) ? payload.outbound : [];
  const max = maxAbsAcrossSeries([inbound, outbound], fromUnit);
  const { unit, scale } = chooseUnit(max);
  return {
    unit,
    inbound: scaleSeries(inbound, fromUnit, scale),
    outbound: scaleSeries(outbound, fromUnit, scale),
  };
}

/**
 * Normalize a DCI payload (two ports) for display so both share a single unit.
 */
export function normalizeDciBandwidthForDisplay(
  portA: { inbound?: BandwidthPoint[]; outbound?: BandwidthPoint[]; unit?: string } = {},
  portB: { inbound?: BandwidthPoint[]; outbound?: BandwidthPoint[]; unit?: string } = {},
  rootUnit?: string
): {
  unit: BandwidthUnit;
  portA: { inbound: BandwidthPoint[]; outbound: BandwidthPoint[] };
  portB: { inbound: BandwidthPoint[]; outbound: BandwidthPoint[] };
} {
  const fromUnit = rootUnit ?? portA.unit ?? portB.unit ?? "Mbps";
  const inboundA = Array.isArray(portA.inbound) ? portA.inbound : [];
  const outboundA = Array.isArray(portA.outbound) ? portA.outbound : [];
  const inboundB = Array.isArray(portB.inbound) ? portB.inbound : [];
  const outboundB = Array.isArray(portB.outbound) ? portB.outbound : [];
  const max = maxAbsAcrossSeries([inboundA, outboundA, inboundB, outboundB], fromUnit);
  const { unit, scale } = chooseUnit(max);
  return {
    unit,
    portA: {
      inbound: scaleSeries(inboundA, fromUnit, scale),
      outbound: scaleSeries(outboundA, fromUnit, scale),
    },
    portB: {
      inbound: scaleSeries(inboundB, fromUnit, scale),
      outbound: scaleSeries(outboundB, fromUnit, scale),
    },
  };
}
